// Added to Cart Popup Handler
class AddedToCartPopup {
  constructor() {
    this.popup = document.getElementById("added-to-cart-popup");
    if (!this.popup) return;

    this.closeButton = this.popup.querySelector(".popup-close");
    this.continueShoppingButton = this.popup.querySelector(
      ".popup-continue-shopping-btn",
    );

    this.productImage = document.getElementById("popup-product-image");
    this.productTitle = document.getElementById("popup-product-title");
    this.productPrice = document.getElementById("popup-product-price");
    this.productQuantity = document.getElementById("popup-product-quantity");

    this.init();
  }

  init() {
    // Listen for close button clicks
    if (this.closeButton) {
      this.closeButton.addEventListener("click", () => this.hide());
    }

    // Listen for continue shopping button clicks
    if (this.continueShoppingButton) {
      this.continueShoppingButton.addEventListener("click", () => this.hide());
    }

    // Listen for clicks outside the popup content
    this.popup.addEventListener("click", (event) => {
      if (event.target === this.popup) {
        this.hide();
      }
    });

    // Listen for ESC key
    document.addEventListener("keyup", (event) => {
      if (
        event.code.toUpperCase() === "ESCAPE" &&
        this.popup.classList.contains("active")
      ) {
        this.hide();
      }
    });

    // Hook into Shopify's form submit
    this.attachToForms();
  }

  attachToForms() {
    // Listen for all product forms
    document.querySelectorAll('form[action*="/cart/add"]').forEach((form) => {
      form.addEventListener("submit", async (event) => {
        event.preventDefault();

        // Check for location conflict before proceeding
        const locationSelectorEl = document.querySelector('[data-location-selector]');
        let selectedLocation = null;
        if (locationSelectorEl) {
          const checkedRadio = locationSelectorEl.querySelector('input[name="location"]:checked');
          selectedLocation = checkedRadio ? checkedRadio.value : null;
          if (selectedLocation) {
            const canProceed = await this.checkLocationConflict(selectedLocation);
            if (!canProceed) return;
          }
        }

        const formData = new FormData(form);
        const submitButton = form.querySelector('[type="submit"]');

        let originalText = "";

        // Disable submit button
        if (submitButton) {
          submitButton.disabled = true;
          originalText = submitButton.textContent;
          submitButton.textContent = "Adding...";
        }

        try {
          const response = await fetch("/cart/add.js", {
            method: "POST",
            body: formData,
          });

          if (response.ok) {
            const data = await response.json();

            this.show(data);

            // Publish cart update event if using pubsub
            if (
              typeof publish !== "undefined" &&
              typeof PUB_SUB_EVENTS !== "undefined"
            ) {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: "product-form",
                cartData: data,
              });
            }

            // Update cart count if it exists
            this.updateCartCount();
          } else {
            console.error("Error adding to cart");
          }
        } catch (error) {
          console.error("Error:", error);
        } finally {
          // Re-enable submit button
          if (submitButton && originalText) {
            submitButton.disabled = false;
            submitButton.textContent = originalText;
          }
        }
      });
    });
  }

  async checkLocationConflict(selectedLocation) {
    let cartLocation = null;
    try {
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      if (cart.item_count === 0) return true;
      // Find the first cart item that has a "Location" option and read its value
      for (const item of cart.items) {
        const opts = item.options_with_values || [];
        const match = opts.find(o => (o.name || '').toLowerCase() === 'location');
        if (match && match.value) {
          cartLocation = match.value;
          break;
        }
      }
    } catch (e) {
      return true;
    }

    if (!cartLocation || cartLocation === selectedLocation) return true;

    const confirmed = await this.showRegionChangeModal();
    if (!confirmed) return false;

    try {
      await fetch('/cart/clear.js', { method: 'POST' });
      await this.updateCartCount();
    } catch (e) {
      console.error('Error clearing cart:', e);
    }

    return true;
  }

  showRegionChangeModal() {
    return new Promise((resolve) => {
      const modal = document.getElementById('location-change-modal');
      if (!modal) { resolve(true); return; }

      modal.classList.add('is-open');
      document.body.style.overflow = 'hidden';

      const done = (val) => {
        modal.classList.remove('is-open');
        document.body.style.overflow = '';
        resolve(val);
      };

      const confirmBtn = modal.querySelector('[data-confirm-region-change]');
      const cancelBtns = modal.querySelectorAll('[data-cancel-region-change]');
      const overlay = modal.querySelector('.Modal-overlay');

      confirmBtn?.addEventListener('click', () => done(true), { once: true });
      cancelBtns?.forEach(btn => btn.addEventListener('click', () => done(false), { once: true }));
      overlay?.addEventListener('click', () => done(false), { once: true });

      const escHandler = (e) => {
        if (e.key === 'Escape') { document.removeEventListener('keydown', escHandler); done(false); }
      };
      document.addEventListener('keydown', escHandler);
    });
  }

  async updateCartCount() {
    try {
      const response = await fetch("/cart.js");
      const cart = await response.json();

      // Update cart icon bubble if it exists
      const cartBubble = document.querySelector(".cart-count-bubble span");
      if (cartBubble) {
        cartBubble.textContent = cart.item_count;
      }
    } catch (error) {
      console.error("Error updating cart count:", error);
    }
  }

  getSizedImageUrl(url, size = '400x') {
    if (!url) return url;

    // Extract the file extension
    const match = url.match(/\.(jpg|jpeg|png|gif|webp)(\?|$)/i);
    if (!match) return url;

    // Insert size parameter before the file extension
    return url.replace(match[0], `_${size}${match[0]}`);
  }

  show(productData) {
    if (!this.popup) return;

    // Update popup content
    if (this.productImage && productData.featured_image) {
      this.productImage.src = this.getSizedImageUrl(productData.featured_image.url, '400x');
      this.productImage.alt =
        productData.featured_image.alt || productData.product_title;
    }

    if (this.productTitle) {
      this.productTitle.textContent = productData.product_title;
    }

    if (this.productPrice) {
      this.productPrice.textContent = this.formatMoney(productData.price);
    }

    if (this.productQuantity) {
      this.productQuantity.textContent = productData.quantity || 1;
    }

    // Show popup
    this.popup.style.display = "flex";
    setTimeout(() => {
      this.popup.classList.add("active");
    }, 10);

    // Lock body scroll
    document.body.style.overflow = "hidden";
  }

  hide() {
    if (!this.popup) return;

    this.popup.classList.remove("active");
    setTimeout(() => {
      this.popup.style.display = "none";
    }, 300);

    // Unlock body scroll
    document.body.style.overflow = "";
  }

  formatMoney(cents) {
    if (typeof Shopify !== "undefined" && Shopify.formatMoney) {
      return Shopify.formatMoney(
        cents,
        window.theme?.moneyFormat || "${{amount}}",
      );
    }

    // Fallback formatting
    const dollars = (cents / 100).toFixed(2);
    return `$${dollars}`;
  }
}

// Initialize when DOM is ready
document.addEventListener("DOMContentLoaded", () => {
  new AddedToCartPopup();
});
